import { useState, useRef } from "react";
import { useFormikContext } from "formik";
import ChevronDownArrow from "@app/components/select/chevron-down";
import { BookOption, BookOptions } from "@app/constants";
import { CreateBookForm } from "@app/types";
import useOnClickOutside from "@core/hooks/use-on-click-outside.hook";

interface BookOptionSelectProps {
  name?: string;
  onChange?: (value: number | string) => void;
  fmOnChange?: (value: number | string) => void;
  [key: string]: any;
}

export function BookOptionSelect(props: BookOptionSelectProps) {
  const formik = useFormikContext<CreateBookForm>();
  const currentValue = formik.values.bookOption;
  const [isOpen, setIsOpen] = useState(false);

  // Xác định màu nền và màu chữ dựa trên option được chọn
  const getColors = () => {
    const freeValue = BookOption.FREE.toString();
    const vipValue = BookOption.VIP.toString();
    const purchaseValue = BookOption.PURCHASE.toString();
    
    if (currentValue === freeValue) {
      return { bg: "#e6ffed", text: "#00a653" }; // FREE: màu nền xanh lá nhạt, chữ xanh đậm
    } else if (currentValue === vipValue) {
      return { bg: "#fffee6", text: "#ffd500" }; // VIP: màu nền vàng nhạt, chữ vàng
    } else if (currentValue === purchaseValue) {
      return { bg: "#ffe4d6", text: "#ff7029" }; // PURCHASE: màu nền cam nhạt, chữ cam
    }

    return { bg: "white", text: "#000000" }; // Màu mặc định
  };

  const getOptionColor = (optionValue: number | string) => {
    const freeValue = BookOption.FREE.toString();
    const vipValue = BookOption.VIP.toString();
    const purchaseValue = BookOption.PURCHASE.toString();
    
    if (optionValue.toString() === freeValue) {
      return { bg: "#e6ffed", hover: "#d0ffdf", text: "#00a653" }; // FREE
    } else if (optionValue.toString() === vipValue) {
      return { bg: "#fffee6", hover: "#fffcd1", text: "#ffd500" }; // VIP
    } else if (optionValue.toString() === purchaseValue) {
      return { bg: "#ffe4d6", hover: "#ffdcc5", text: "#ff7029" }; // PURCHASE
    }
    
    return { bg: "white", hover: "#F5F5F5", text: "#000000" };
  };

  const currentColors = getColors();
  const selectBoxRef = useRef(null);

  useOnClickOutside(selectBoxRef, () => {
    setIsOpen(false);
  });

  const handleOptionClick = (value: number | string) => {
    // Update Formik form value
    if (formik && props.name) {
      formik.setFieldValue(props.name, value);
    }
    
    // Call onChange callbacks
    if (props.onChange) {
      props.onChange(value);
    }
    
    if (props.fmOnChange) {
      props.fmOnChange(value);
    }
    
    setIsOpen(false);
  };

  const selectedLabel = BookOptions.find(opt => opt.value === currentValue)?.label || "Lựa chọn cách phân phối";

  return (
    <div className="relative" ref={selectBoxRef}>
      <div
        style={{ 
          backgroundColor: currentColors.bg, 
          color: currentColors.text,
          borderRadius: "8px",
          border: !currentValue ? "1px solid rgba(0, 0, 0, 0.2)" : "none"
        }}
        className="flex items-center h-[52px] cursor-pointer px-3"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="flex flex-1 items-center justify-start leading-[1.5] !mx-3">
          {currentValue ? selectedLabel : <span className="text-[#A0A7B3]">Lựa chọn cách phân phối</span>}
        </div>
        <ChevronDownArrow />
      </div>

      {isOpen && (
        <div className="z-10 top-[52px] absolute bg-white border border-solid border-[#e0e0e0] shadow-lg rounded-lg mt-2 w-full overflow-hidden">
          {BookOptions.map((opt, index) => {
            const colors = getOptionColor(opt.value);

            return (
              <div
                key={index}
                style={{
                  backgroundColor: currentValue === opt.value ? colors.bg : "white",
                  color: colors.text
                }}
                onMouseEnter={(e) => {
                  if (currentValue !== opt.value) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = colors.hover;
                  }
                }}
                onMouseLeave={(e) => {
                  if (currentValue !== opt.value) {
                    (e.currentTarget as HTMLElement).style.backgroundColor = "white";
                  }
                }}
                className="px-3 py-3 border-b border-solid border-[#e0e0e0] cursor-pointer whitespace-nowrap"
                onClick={() => handleOptionClick(opt.value)}
              >
                {opt.label}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
